<template>
  <v-container>
    <v-card>
      <v-row align="center" class="py-1 px-2" no-gutters>
        <v-col cols="12" sm="6">
          <v-breadcrumbs class="px-2" :items="breadcrumbsItems"></v-breadcrumbs>
        </v-col>
        <v-col
          cols="12"
          sm="6"
          :class="$store.getters.LANGUAGE == 'fa' ? 'text-left' : 'text-right'"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                fab
                small
                class="mx-2"
                v-bind="attrs"
                v-on="on"
                color="orange"
                @click="helpDialog = true"
              >
                <v-icon small>fa-question</v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.translator("showHelp") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-container>
        <v-form ref="form" v-model="valid" @submit.prevent="getStats">
          <v-row align="end">
            <v-col cols="12" sm="4">
              <VuePersianDatetimePicker
                type="date"
                input-class="vpdp-input"
                format="YYYY-MM-DD"
                placeholder="YYYY/MM/DD"
                v-model="searchFields.date_from"
                :label="$vuetify.lang.translator('dateFrom')"
                :editable="true"
                :locale="$store.getters.LANGUAGE == 'fa' ? 'fa' : 'en'"
                :display-format="
                  $store.getters.LANGUAGE == 'fa'
                    ? 'jYYYY/jMM/jDD'
                    : 'YYYY/MM/DD'
                "
              ></VuePersianDatetimePicker>
            </v-col>
            <v-col cols="12" sm="4">
              <VuePersianDatetimePicker
                type="date"
                input-class="vpdp-input"
                format="YYYY-MM-DD"
                placeholder="YYYY/MM/DD"
                v-model="searchFields.date_to"
                :label="$vuetify.lang.translator('dateTo')"
                :editable="true"
                :locale="$store.getters.LANGUAGE == 'fa' ? 'fa' : 'en'"
                :display-format="
                  $store.getters.LANGUAGE == 'fa'
                    ? 'jYYYY/jMM/jDD'
                    : 'YYYY/MM/DD'
                "
              ></VuePersianDatetimePicker>
            </v-col>
            <v-col cols="12" sm="4">
              <v-btn
                color="primary"
                :loading="loading"
                :disabled="loading"
                block
                type="submit"
              >
                <v-icon class="mx-2">fa-sync-alt</v-icon
                >{{ $vuetify.lang.translator("updateStatistics") }}</v-btn
              >
            </v-col>
          </v-row>
        </v-form>
      </v-container>

      <v-row>
        <v-col cols="12" sm="6">
          <v-card flat v-if="imagesLoading">
            <div class="text-center my-3">
              {{ $vuetify.lang.translator("imagesWithWithoutErrors") }}
            </div>
            <v-progress-linear indeterminate></v-progress-linear>
          </v-card>
          <pie-chart
            :data="imagesData"
            :options="imagesOptions"
            :labels="imagesLabels"
            :colors="imagesColors"
            v-else
          ></pie-chart>
        </v-col>
        <v-col cols="12" sm="6">
          <v-card flat v-if="errorTypesLoading">
            <div class="text-center my-3">
              {{ $vuetify.lang.translator("numberOfErrorTypes") }}
            </div>
            <v-progress-linear indeterminate></v-progress-linear>
          </v-card>
          <pie-chart
            :data="errorTypesData"
            :options="errorTypesOptions"
            :labels="errorTypesLabels"
            :colors="errorTypesColors"
            v-else
          ></pie-chart>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6">
          <v-card flat v-if="employersImagesLoading">
            <div class="text-center my-3">
              {{ $vuetify.lang.translator("employersImages") }}
            </div>
            <v-progress-linear indeterminate></v-progress-linear>
          </v-card>
          <bar-chart
            :data="employersImagesData"
            :options="employersImagesOptions"
            :labels="employersImagesLabels"
            v-else
          ></bar-chart>
        </v-col>
        <v-col cols="12" sm="6">
          <v-card flat v-if="errorsFrequencyLoading">
            <div class="text-center my-3">
              {{ $vuetify.lang.translator("errorsFrequency") }}
            </div>
            <v-progress-linear indeterminate></v-progress-linear>
          </v-card>
          <bar-chart
            :data="errorsFrequencyData"
            :options="errorsFrequencyOptions"
            :labels="errorsFrequencyLabels"
            v-else
          ></bar-chart>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog
      v-model="helpDialog"
      persistent
      max-width="800"
      @keydown.esc="helpDialog = false"
      transition="dialog-top-transition"
    >
      <v-card>
        <v-toolbar dense elevation="2">
          <v-toolbar-title>{{
            $vuetify.lang.translator("help")
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="helpDialog = false">
              <v-icon>fa-times</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <div class="mt-6">
            {{ $vuetify.lang.translator("dashboardHelp") }}
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn color="success" @click="helpDialog = false">{{
            $vuetify.lang.translator("gotIt")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { handleError } from "@/helpers/helper";
import VuePersianDatetimePicker from "vue-persian-datetime-picker";
import BarChart from "@/components/BarChart";
import PieChart from "@/components/PieChart";

export default {
  name: "Dashboard",
  components: {
    VuePersianDatetimePicker,
    BarChart,
    PieChart
  },
  data: () => ({
    breadcrumbsItems: [],
    valid: false,
    searchFields: {
      date_from: null,
      date_to: null
    },
    loading: false,
    helpDialog: false,
    imagesLoading: false,
    imagesData: [],
    imagesOptions: {},
    imagesLabels: [],
    imagesColors: ["red", "green"],
    errorTypesLoading: false,
    errorTypesData: [],
    errorTypesOptions: {},
    errorTypesLabels: [],
    errorTypesColors: ["yellow", "orange", "red"],
    employersImagesLoading: false,
    employersImagesData: [],
    employersImagesOptions: {},
    employersImagesLabels: [],
    errorsFrequencyLoading: false,
    errorsFrequencyData: [],
    errorsFrequencyOptions: {},
    errorsFrequencyLabels: []
  }),
  created() {
    this.getCurrentTime();
    this.breadcrumbsItems.push({
      text: this.$vuetify.lang.translator("dashboard"),
      to: { name: "Dashboard" }
    });
    this.imagesOptions = {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        labels: {
          fontFamily: "'Vazir FD', Tahoma"
        }
      },
      title: {
        display: true,
        text: this.$vuetify.lang.translator("imagesWithWithoutErrors"),
        fontFamily: "'Vazir FD', Tahoma"
      }
    };
    this.imagesLabels.push(this.$vuetify.lang.translator("withError"));
    this.imagesLabels.push(this.$vuetify.lang.translator("withoutError"));
    this.errorTypesOptions = {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        labels: {
          fontFamily: "'Vazir FD', Tahoma"
        }
      },
      title: {
        display: true,
        align: "center",
        text: this.$vuetify.lang.translator("numberOfErrorTypes"),
        fontFamily: "'Vazir FD', Tahoma"
      }
    };
    this.errorTypesLabels.push(this.$vuetify.lang.translator("normal"));
    this.errorTypesLabels.push(this.$vuetify.lang.translator("warning"));
    this.errorTypesLabels.push(this.$vuetify.lang.translator("critical"));
    this.employersImagesOptions = {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      title: {
        display: true,
        text: this.$vuetify.lang.translator("employersImages"),
        fontFamily: "'Vazir FD', Tahoma"
      },
      scales: {
        xAxes: [
          {
            display: true,
            scaleLabel: {
              display: true,
              labelString: this.$vuetify.lang.translator("employer"),
              fontFamily: "'Vazir FD', Tahoma"
            }
          }
        ],
        yAxes: [
          {
            display: true,
            scaleLabel: {
              display: true,
              labelString: this.$vuetify.lang.translator("number"),
              fontFamily: "'Vazir FD', Tahoma"
            }
          }
        ]
      }
    };
    this.errorsFrequencyOptions = {
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      title: {
        display: true,
        text: this.$vuetify.lang.translator("errorsFrequency"),
        fontFamily: "'Vazir FD', Tahoma"
      },
      scales: {
        xAxes: [
          {
            display: true,
            scaleLabel: {
              display: true,
              labelString: this.$vuetify.lang.translator("error"),
              fontFamily: "'Vazir FD', Tahoma"
            }
          }
        ],
        yAxes: [
          {
            display: true,
            scaleLabel: {
              display: true,
              labelString: this.$vuetify.lang.translator("number"),
              fontFamily: "'Vazir FD', Tahoma"
            }
          }
        ]
      }
    };
  },
  methods: {
    getCurrentTime() {
      var self = this;
      this.$http
        .get("/stats/current-date/")
        .then(function(response) {
          let today = new Date(response.data.today);
          let lastWeek = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - today.getDay() - 6
          );
          self.searchFields.date_from = [
            lastWeek.getFullYear(),
            ("0" + (lastWeek.getMonth() + 1)).slice(-2),
            ("0" + lastWeek.getDate()).slice(-2)
          ].join("-");
          self.searchFields.date_to = [
            today.getFullYear(),
            ("0" + (today.getMonth() + 1)).slice(-2),
            ("0" + today.getDate()).slice(-2)
          ].join("-");
          self.getStats();
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        });
    },
    getStats() {
      this.getImagesData();
      this.getErrorTypesData();
      this.getEmployersImagesData();
      this.getErrorsFrequencyData();
    },
    getImagesData() {
      this.imagesLoading = true;
      this.imagesData = [];
      var self = this;
      this.$http
        .post("/stats/images/", this.searchFields)
        .then(function(response) {
          self.imagesData.push(response.data.with_errors);
          self.imagesData.push(response.data.without_errors);
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.imagesLoading = false;
        });
    },
    getErrorTypesData() {
      this.errorTypesLoading = true;
      this.errorTypesData = [];
      var self = this;
      this.$http
        .post("/stats/error-types/", this.searchFields)
        .then(function(response) {
          self.errorTypesData.push(response.data.normals);
          self.errorTypesData.push(response.data.warnings);
          self.errorTypesData.push(response.data.critics);
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.errorTypesLoading = false;
        });
    },
    getEmployersImagesData() {
      this.employersImagesLoading = true;
      this.employersImagesData = [];
      this.employersImagesLabels = [];
      var self = this;
      this.$http
        .post("/stats/employers-images/", this.searchFields)
        .then(function(response) {
          for (let index = 0; index < response.data.length; index++) {
            self.employersImagesData.push(response.data[index].id__count);
            self.employersImagesLabels.push(
              response.data[index].batch__employer__name
            );
          }
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.employersImagesLoading = false;
        });
    },
    getErrorsFrequencyData() {
      this.errorsFrequencyLoading = true;
      this.errorsFrequencyData = [];
      this.errorsFrequencyLabels = [];
      var self = this;
      this.$http
        .post("/stats/errors-frequency/", this.searchFields)
        .then(function(response) {
          for (let index = 0; index < response.data.length; index++) {
            self.errorsFrequencyData.push(response.data[index].name__count);
            self.errorsFrequencyLabels.push(response.data[index].name);
          }
        })
        .catch(function(error) {
          let result = handleError(
            error,
            self.$toasted,
            self.$router,
            self.$store
          );
          for (var item in result) {
            self.$toasted.error(result[item], {
              duration: 5000,
              keepOnHover: true,
              className:
                self.$store.getters.LANGUAGE == "fa"
                  ? "vazir-font-fd"
                  : "vazir-font",
              iconPack: "fontawesome",
              icon: "exclamation-triangle",
              position: "top-center"
            });
          }
        })
        .then(function() {
          self.errorsFrequencyLoading = false;
        });
    }
  }
};
</script>
